import { Component, type ErrorInfo, type ReactNode } from 'react'

import { ErrorState } from '../components'

interface State {
  error: Error | null
}

/**
 * The last resort. A render crash in one console must not blank the other, and
 * it must still hand the reader an id they can quote — so the boundary invents
 * one rather than showing a bare stack.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Deliberately console, not a service: there is no telemetry endpoint yet.
    console.error('Console render failed', error, info.componentStack)
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="p-10">
        <ErrorState
          error={{
            code: 'INTERNAL',
            message: error.message,
            requestId: 'client-render',
          }}
          onRetry={() => this.setState({ error: null })}
        />
      </div>
    )
  }
}
